import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import styles from './styles.module.scss'
import Carousel from 'srcReact/components/common2/Carousel'
import MixrCard from 'srcReact/components/common2/MixrCard'
import MixrCardApprove from 'srcReact/components/common2/MixrCardApprove'
import NotFound from 'srcReact/components/common2/NotFound'
import { myhomeActions } from 'srcReact/MyHomePage'
import history from 'srcReact/helpers/history'
import Spinner from 'srcReact/components/common2/Spinner'
import { decode } from 'html-entities/lib/index'
import { checkNested } from 'srcReact/helpers/validator'
import { urlSearchParams } from 'srcReact/helpers/utils'
import { isEventTimeDiffLessThan5minutes } from 'srcReact/helpers/datetimeHelper'
import useInfinitScroll from 'srcReact/helpers/hooks/useInfinitScroll'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import { canCreateGroup } from 'srcReact/helpers/userHelper'
import { rsvpActions } from 'srcReact/components/common2/RSVP/actions'
import useInterval from 'srcReact/helpers/hooks/useInterval'

const MyMixrs = ({ isMobile, orderBy }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    common: { isNative, userLocation, isDesktop },
    rsvp: { loading: rsvpLoading, success: rsvpSuccess, error: rsvpError, data: rsvpData },
    myhome: { all, myMixrsLoading },
    organization: { current_organization }
  } = useSelector((state) => state)
  const [futurePagination, setFuturePagination] = useState({
    nextOffset: 0,
    isPagination: false
  })
  const [pastPagination, setPastPagination] = useState({
    nextOffset: 0,
    isPagination: false
  })

  const [suggestedPagination, setSuggestedPagination] = useState({
    nextOffset: 0,
    isPagination: false
  })

  useEffect(() => {
    if (!rsvpLoading && rsvpSuccess && rsvpData) {
      setFuturePagination({ nextOffset: all.future_mixr_list.length, isPagination: true })
      dispatch(myhomeActions.updateRSVPData(rsvpData.mixr_id, rsvpData, futurePagination.nextOffset))
      dispatch(rsvpActions.clearData())
    }
  }, [rsvpLoading, rsvpSuccess])

  useEffect(() => {
    // nativeapp
    if (isNative && all.pagination.isPagination) {
      dispatch(
        myhomeActions.getMyHomeMixrFutureList(
          urlSearchParams('id_workplace').toString(),
          urlSearchParams('lat'),
          urlSearchParams('lon'),
          all.pagination.nextOffset
        )
      )
      return
    }

    if (checkNested(current_organization, 'info', 'my_access', 'role_as_workplace_member')) {
      const workplaceIds = current_organization.info.my_access.role_as_workplace_member.workplace_list
      if (workplaceIds && workplaceIds.length >= 1 && all.pagination.isPagination && userLocation) {
        dispatch(
          myhomeActions.getMyHomeMixrFutureList(
            workplaceIds.toString(),
            userLocation.latitude,
            userLocation.longitude,
            all.pagination.nextOffset
          )
        )
      }
    }
  }, [all.pagination])

  useEffect(() => {
    // nativeapp
    if (isNative && futurePagination.isPagination) {
      handleFutureMixrsPagination(
        urlSearchParams('id_workplace').toString(),
        urlSearchParams('lat'),
        urlSearchParams('lon')
      )
      return
    }

    if (checkNested(current_organization, 'info', 'my_access', 'role_as_workplace_member')) {
      const workplaceIds = current_organization.info.my_access.role_as_workplace_member.workplace_list
      if (workplaceIds && workplaceIds.length >= 1 && futurePagination.isPagination && userLocation) {
        handleFutureMixrsPagination(workplaceIds, userLocation.latitude, userLocation.longitude)
      }
    }
  }, [futurePagination])

  const handleFutureMixrsPagination = (workplaceIds, lat, long) => {
    dispatch(
      myhomeActions.getMyHomeMixrFutureListPagination(workplaceIds.toString(), lat, long, futurePagination.nextOffset)
    )
    setFuturePagination({
      ...futurePagination,
      isPagination: false
    })
  }

  const handleSuggestedMixrsPagination = (workplaceIds, lat, long) => {
    dispatch(myhomeActions.getMyHomeSuggestedMixrs(workplaceIds.toString(), lat, long, suggestedPagination.nextOffset))
    setSuggestedPagination({
      ...suggestedPagination,
      isPagination: false
    })
  }

  useEffect(() => {
    if (suggestedPagination.isPagination) {
      if (isNative) {
        handleSuggestedMixrsPagination(
          urlSearchParams('id_workplace').toString(),
          urlSearchParams('lat'),
          urlSearchParams('lon')
        )
        return
      }

      if (checkNested(current_organization, 'info', 'my_access', 'role_as_workplace_member')) {
        const workplaceIds = current_organization.info.my_access.role_as_workplace_member.workplace_list
        if (workplaceIds && workplaceIds.length >= 1 && userLocation) {
          handleSuggestedMixrsPagination(workplaceIds, userLocation.latitude, userLocation.longitude)
        }
      }
    }
  }, [suggestedPagination])

  const lastMixrCardRef = useInfinitScroll(
    myMixrsLoading,
    all.stats.total_mixrs_future - all.future_mixr_list.length,
    all.future_mixr_list.length,
    setFuturePagination,
    [myMixrsLoading]
  )
  const lastSuggestedMixrCardRef = useInfinitScroll(
    myMixrsLoading,
    all.stats.total_mixrs_suggested - all.suggested_mixr_list.length,
    all.suggested_mixr_list.length,
    setSuggestedPagination,
    [myMixrsLoading]
  )

  let mixrDataLists = []
  let currentIndex = 0

  if (appConstantSettings.ENABLE_MYHOME_CAROUSEL_TAB) {
    if (orderBy === 'allmixrs') {
      mixrDataLists = [...all.past_mixr_list, ...all.future_mixr_list]
    } else if (orderBy === 'mymixrs') {
      mixrDataLists = [...all.future_mixr_list]?.filter((m) => m.user_attending_type)
    } else if (orderBy === 'pending') {
      mixrDataLists = [...all.mixr_invitations.invites]
    }
  } else {
    mixrDataLists = [...all.past_mixr_list, ...all.future_mixr_list]
  }

  if (all.future_mixr_list.length >= 1) {
    let index =
      mixrDataLists &&
      mixrDataLists.length > 0 &&
      mixrDataLists.findIndex((obj) => obj.mixr_id === all.future_mixr_list[0]['mixr_id'])
    if (current_organization && canCreateGroup(current_organization) && index !== -1) {
      mixrDataLists.splice(index + (appConstantSettings.CTA_BUTTON_DISPLAY - 1), 0, {
        index: appConstantSettings.CTA_BUTTON_DISPLAY - 1,
        is_cta_button: true
      })
    }
    mixrDataLists = mixrDataLists
    currentIndex = index
  }

  if (all.future_mixr_list.length === 0 && all.past_mixr_list.length >= 1) {
    mixrDataLists = [
      ...mixrDataLists,
      { index: all.past_mixr_list && all.past_mixr_list.length + 1, is_future_mixr_empty: true }
    ]
    currentIndex = all.past_mixr_list && all.past_mixr_list.length - 1
  }

  /* The value of `is_join_call_button` is determined by calling the `isEventTimeDiffLessThan5minutes` function with the arguments `event_utc_start_datetime` and `appConstantSettings.CTA_BUTTON_JOIN_CALL`. The result of the function call is then assigned to the `is_join_call_button` property of mixr */
  useInterval(() => {
    if (mixrDataLists && mixrDataLists.length > 0) {
      mixrDataLists.map((m) => {
        if (!m?.mixr_over && (m?.mixr_attending_type === 'virtual' || m?.mixr_attending_type === 'both')) {
          Object.assign(m, {
            is_join_call_button: isEventTimeDiffLessThan5minutes(
              m.event_utc_start_datetime,
              appConstantSettings.CTA_BUTTON_JOIN_CALL
            )
          })
        }
      })
    }
  }, appConstantSettings.REFRESH_INTERVAL_JOIN_CALL)
  return (
    <div className={styles.mymirsWrapper} data-testid='mymixrs-home'>
      <header className={styles.mixrHeader}>
        {!isMobile && <div className={styles.filter}>{/*filter will go here*/}</div>}
      </header>
      {mixrDataLists && mixrDataLists.length >= 1 ? (
        <Carousel
          initialActive={currentIndex}
          className={styles.carousel}
          itemPadding={isMobile ? [0, 0] : [10, 10]}
          showArrows={isMobile ? false : true}
          prevArrowClass={styles.prevArrow}
          nextArrowClass={styles.nextArrow}
          {...(isMobile
            ? {
                breakPoints: [{ width: 1, itemsToShow: 2.2, itemsToScroll: 1 }]
              }
            : {})}
          afterCarouselElementId={'#mygroups'}
          isArrowEdge={false}>
          {mixrDataLists.map((m, i) => {
            let parentMixrId = null
            if (m.recurrence_type === 'recurring') {
              const suspendMixrObjs = all?.moderation_info?.mixrs?.suspended_mixr_urls
              if (suspendMixrObjs) {
                const entries = Object.entries(suspendMixrObjs)
                const suspendedMixr = entries.find((x) => x[0] == m.mixr_id)
                parentMixrId = suspendedMixr && suspendedMixr[1]
              }
            }
            const showAcceptDecline = m.role_group === 'none' && m.role_mixr === 'none' && m.type === 'invite'
            return showAcceptDecline ? (
              <MixrCardApprove
                mixrId={m.mixr_id}
                infiniteScrollRef={lastMixrCardRef}
                groupId={m.group_id}
                groupName={decode(m.group_name)}
                picture={m.mixr_picture && m.mixr_picture}
                mixrName={decode(m.mixr_title)}
                datetime={m.event_utc_start_datetime}
                isMobile={isMobile || isNative}
              />
            ) : (
              <MixrCard
                key={i}
                infiniteScrollRef={lastMixrCardRef}
                mixrId={m.mixr_id}
                groupId={m.group_id}
                startDate={m.event_utc_start_datetime}
                endDate={m.event_utc_end_datetime}
                utcOffset={m.event_utc_offset}
                timezone={m.timezone}
                timezoneFullName={m.timezone_name}
                location={m.location}
                name={decode(m.group_name)}
                participants={m.number_of_participants}
                isMixrOver={m.mixr_over}
                isMixrCancelled={m.is_cancelled === 1}
                isFutureMixrEmpty={m.is_future_mixr_empty}
                isCtaButton={m.is_cta_button}
                url={m.mixr_url}
                picture={m.mixr_picture && m.mixr_picture}
                notifications={m.notifications}
                title={decode(m.mixr_title)}
                description={m.mixr_description}
                attendance={m.mixr_attending_type}
                userAttendance={m.user_attending_type}
                color={
                  m.mixr_over || m.is_cancelled
                    ? 'gray'
                    : m.mixr_color
                    ? m.mixr_color
                    : appConstantSettings.DEFAULT_COLOR.name
                }
                isVisibility={m.group_visibility !== 'public'}
                isRecurrence={m.recurrence_type === 'recurring'}
                parentMixrId={parentMixrId}
                menuPortalTarget={true}
                showRecurrentIcon={isMobile || isNative ? false : true}
                showPrivateIcon={isMobile || isNative ? false : true}
                mixrRole={m.role_mixr}
                groupRole={m.role_group}
                flaggedList={all.moderation_info.mixrs.flagged_mixr_list}
                isGroupSuspended={
                  checkNested(all, 'moderation_info', 'groups', 'suspended_groups') &&
                  all.moderation_info.groups.suspended_groups.some((x) => x === m.group_id)
                }
                isSuspended={
                  checkNested(all, 'moderation_info', 'mixrs', 'suspended_mixrs') &&
                  all.moderation_info.mixrs.suspended_mixrs.some((x) => x === m.mixr_id)
                }
                withoutGroupApproval={m.without_group_approval}
                isInvited={m.is_invited === 1}
                isJoinCallButton={m.is_join_call_button}
                joinLink={m.join_in_link}
                groupType={m.group_type}
                origin='carousel'
              />
            )
          })}
          {myMixrsLoading && <Spinner customCss={styles.mixrCarouselLoader} />}
        </Carousel>
      ) : (
        mixrDataLists &&
        mixrDataLists.length === 0 &&
        all.loaded && (
          <div className={styles.noMixrsWrapper}>
            <NotFound
              title={t('myhome.mymixrs.notFound.title')}
              button={t('myhome.mymixrs.notFound.button')}
              handleClick={() =>
                isNative ? (window.location = config.webUrl + '/0/discover') : history.push('/0/discover')
              }
              className={`${styles.notFound} ${all.suggested_mixr_list.length === 0 ? styles.notFoundFullWidth : ''}`}
            />
            {all.suggested_mixr_list.length > 0 && (
              <div className={styles.suggestedMixrsContainer}>
                <h3 className={styles.subtitle}>
                  {all.stats.suggested_mixrs_type === 'suggested'
                    ? t('myhome.mymixrs.suggestedMixrsTitle')
                    : t('myhome.mymixrs.popularMixrsTitle')}
                </h3>
                <Carousel
                  initialActive={currentIndex}
                  className={styles.carousel}
                  itemPadding={isMobile ? [0, 0] : [10, 10]}
                  showArrows={isMobile ? false : true}
                  prevArrowClass={styles.prevArrow}
                  nextArrowClass={styles.nextArrow}
                  {...(isMobile
                    ? {
                        breakPoints: [{ width: 1, itemsToShow: 2.2, itemsToScroll: 1 }]
                      }
                    : {})}
                  isArrowEdge={false}>
                  {all.suggested_mixr_list.map((m, i) => {
                    return (
                      <MixrCard
                        key={i}
                        infiniteScrollRef={lastSuggestedMixrCardRef}
                        mixrId={m.id}
                        groupId={m.group_id}
                        startDate={m.event_utc_start_datetime}
                        endDate={m.event_utc_end_datetime}
                        utcOffset={m.event_utc_offset}
                        timezone={m.timezone}
                        timezoneFullName={m.timezone_name}
                        location={m.location}
                        name={decode(m.group_name)}
                        participants={m.number_of_participants}
                        url={m.mixr_url}
                        picture={m.mixr_picture && m.mixr_picture}
                        notifications={m.notifications}
                        title={decode(m.mixr_title)}
                        description={m.mixr_description}
                        attendance={m.mixr_attending_type}
                        color={
                          m.mixr_over ? 'gray' : m.mixr_color ? m.mixr_color : appConstantSettings.DEFAULT_COLOR.name
                        }
                        isVisibility={m.visibility !== 'public'}
                        isRecurrence={m.recurrence_type === 'recurring'}
                        isViewModal={true}
                        showRecurrentIcon={isMobile || isNative ? false : true}
                        showPrivateIcon={isMobile || isNative ? false : true}
                        mixrRole={m.role_mixr}
                        groupRole={m.role_group}
                        withoutGroupApproval={m.without_group_approval}
                        joinLink={m.join_in_link}
                        origin='carousel'
                      />
                    )
                  })}
                  {myMixrsLoading && <Spinner customCss={styles.mixrCarouselLoader} />}
                </Carousel>
              </div>
            )}
          </div>
        )
      )}
    </div>
  )
}

export default MyMixrs
